import type { TypographyAppearance, TypographyBadgeConfig, TypographyConfig, TypographyEffect, TypographyFontChoice, TypographyFrame, TypographyLayoutMode, TypographyProductPolicy, TypographyShadowLayer } from './types';

const LAYOUTS: TypographyLayoutMode[] = ['arc', 'circle', 'wave', 'rise', 'flag', 'bulge'];
const EFFECTS: TypographyEffect[] = ['gradient', 'outline', 'shadow', 'extrude'];

/** Canonical badge geometry used by admin preview, designer, and exports. */
export const TYPOGRAPHY_BADGE_FRAME: Readonly<TypographyFrame> = Object.freeze({ width: 320, height: 280 });

export function defaultAppearance(color = '#111827'): TypographyAppearance {
  return {
    fill: { type: 'solid', color, colors: [color, '#38bdf8'], angle: 0 },
    stroke: { enabled: false, color: '#ffffff', width: 2 },
    shadow: { enabled: false, layers: [{ color: '#111827', x: 4, y: 4, blur: 0, spread: 0 }] },
    extrude: { enabled: false, color: '#334155', depth: 8, angle: 45, steps: 8 },
  };
}

export function defaultTypographyConfig(color = '#111827'): TypographyConfig {
  return {
    version: 2,
    layout: {
      mode: 'arc', spacing: 0, reverse: false, side: 'outside', arcAngle: 60,
      circleSweep: 360, amplitude: 30, frequency: 1, phase: 0, slope: 20, bulge: 30,
      autoFit: { enabled: true, minSize: 10, maxSize: 120, maxChars: 40 },
      curveVersion: 2,
    },
    appearance: defaultAppearance(color),
  };
}

export function defaultBadgeConfig(): TypographyBadgeConfig {
  const region = (text: string, size: number) => ({ text, fontFamily: 'Roboto', fontSize: size, fontWeight: '700', color: '#111827', spacing: 1 });
  return {
    version: 2,
    top: region('YOUR BRAND', 28),
    bottom: region('ESTABLISHED 2026', 18),
    center: { ...region('VEYO', 36), enabled: true },
    geometry: {
      shape: 'circle', topSweep: 120, bottomSweep: 110, topSide: 'outside', bottomSide: 'outside', padding: 18,
      autoFit: { enabled: true, minSize: 8, maxSize: 72 },
      curveVersion: 2,
    },
    separator: { kind: 'star', color: '#111827', size: 8 },
    appearance: defaultAppearance('#111827'),
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function num(value: unknown, fallback: number, min: number, max: number): number {
  const valueNumber = Number(value);
  return Math.max(min, Math.min(max, Number.isFinite(valueNumber) ? valueNumber : fallback));
}

function color(value: unknown, fallback: string): string {
  const candidate = String(value ?? '').trim();
  return /^#[0-9a-f]{3,8}$/i.test(candidate) ? candidate : fallback;
}

function normalizeShadowLayer(raw: unknown, fallbackColor: string): TypographyShadowLayer {
  const layer = record(raw);
  return {
    color: color(layer.color, fallbackColor),
    x: num(layer.x, 4, -100, 100),
    y: num(layer.y, 4, -100, 100),
    blur: num(layer.blur, 0, 0, 60),
    spread: num(layer.spread, 0, 0, 20),
  };
}

const FILL_TYPES = ['solid', 'linear-gradient', 'radial-gradient', 'none'] as const;

export function normalizeAppearance(raw: unknown, fallbackColor = '#111827'): TypographyAppearance {
  const value = record(raw);
  const defaults = defaultAppearance(fallbackColor);
  const fill = record(value.fill);
  const stroke = record(value.stroke);
  const shadow = record(value.shadow);
  const extrude = record(value.extrude);
  const colors = Array.isArray(fill.colors) ? fill.colors.map((entry) => color(entry, fallbackColor)).slice(0, 6) : defaults.fill.colors;
  const fillType = FILL_TYPES.includes(fill.type as typeof FILL_TYPES[number]) ? fill.type as typeof FILL_TYPES[number] : 'solid';
  // Legacy configs stored a single shadow object; keep accepting it forever.
  const layers = Array.isArray(shadow.layers)
    ? shadow.layers.slice(0, 6).map((layer) => normalizeShadowLayer(layer, defaults.shadow.layers[0].color))
    : [normalizeShadowLayer(shadow, defaults.shadow.layers[0].color)];
  return {
    fill: {
      type: fillType,
      color: color(fill.color, fallbackColor), colors: colors.length >= 2 ? colors : defaults.fill.colors,
      angle: num(fill.angle, 0, -360, 360),
    },
    // A hollow fill without a stroke would be invisible; force the outline on.
    stroke: { enabled: fillType === 'none' || Boolean(stroke.enabled), color: color(stroke.color, defaults.stroke.color), width: num(stroke.width, 2, 0, 30) },
    shadow: { enabled: Boolean(shadow.enabled), layers: layers.length ? layers : defaults.shadow.layers },
    extrude: {
      enabled: Boolean(extrude.enabled), color: color(extrude.color, defaults.extrude.color),
      depth: num(extrude.depth, 8, 0, 100), angle: num(extrude.angle, 45, -360, 360), steps: Math.round(num(extrude.steps, 8, 1, 30)),
    },
  };
}

function normalizeFontChoice(raw: unknown): TypographyFontChoice | null {
  const value = record(raw);
  const family = String(value.family ?? '').trim().slice(0, 80);
  if (!family) return null;
  const numeric = Number(value.weight);
  const weight = Number.isFinite(numeric) ? String(Math.max(100, Math.min(900, Math.round(numeric / 100) * 100))) : '400';
  return { family, weight };
}

export function normalizeTypography(raw: unknown, fallbackColor = '#111827'): TypographyConfig {
  const value = record(raw);
  const layout = record(value.layout);
  const defaults = defaultTypographyConfig(fallbackColor);
  const mode = LAYOUTS.includes(layout.mode as TypographyLayoutMode) ? layout.mode as TypographyLayoutMode : defaults.layout.mode;
  const autoFit = record(layout.autoFit);
  const autoFitMin = num(autoFit.minSize, 10, 4, 300);
  const autoFitMax = Math.max(autoFitMin, num(autoFit.maxSize, 120, 4, 600));
  const font = normalizeFontChoice(value.font);
  return {
    version: 2,
    ...(value.presetId ? { presetId: String(value.presetId) } : {}),
    ...(font ? { font } : {}),
    layout: {
      mode, spacing: num(layout.spacing, 0, -20, 100), reverse: Boolean(layout.reverse), side: layout.side === 'inside' ? 'inside' : 'outside',
      arcAngle: num(layout.arcAngle, 60, -360, 360), circleSweep: num(layout.circleSweep, 360, 45, 360),
      amplitude: num(layout.amplitude, 30, -100, 100), frequency: num(layout.frequency, 1, 0.25, 4), phase: num(layout.phase, 0, -360, 360),
      slope: num(layout.slope, 20, -60, 60), bulge: num(layout.bulge, 30, -100, 100),
      autoFit: {
        enabled: autoFit.enabled !== false, minSize: autoFitMin, maxSize: autoFitMax,
        maxChars: Math.round(num(autoFit.maxChars, 40, 1, 500)),
      },
      curveVersion: layout.curveVersion === 2 ? 2 : 1,
    },
    appearance: normalizeAppearance(value.appearance, fallbackColor),
  };
}

export function normalizeBadge(raw: unknown): TypographyBadgeConfig {
  const value = record(raw);
  const defaults = defaultBadgeConfig();
  const normalizeRegion = (candidate: unknown, fallback: typeof defaults.top) => {
    const row = record(candidate);
    return {
      text: String(row.text ?? fallback.text).slice(0, 120), fontFamily: String(row.fontFamily ?? fallback.fontFamily),
      fontSize: num(row.fontSize, fallback.fontSize, 4, 300), fontWeight: String(row.fontWeight ?? fallback.fontWeight),
      color: color(row.color, fallback.color), spacing: num(row.spacing, fallback.spacing, -20, 100),
    };
  };
  const geometry = record(value.geometry);
  const badgeAutoFit = record(geometry.autoFit);
  const badgeAutoFitMin = num(badgeAutoFit.minSize, 8, 4, 300);
  const badgeAutoFitMax = Math.max(badgeAutoFitMin, num(badgeAutoFit.maxSize, 72, 4, 300));
  const separator = record(value.separator);
  const center = record(value.center);
  const kind = ['none', 'dot', 'star', 'diamond'].includes(String(separator.kind)) ? separator.kind as TypographyBadgeConfig['separator']['kind'] : defaults.separator.kind;
  return {
    version: 2,
    ...(value.presetId ? { presetId: String(value.presetId) } : {}),
    top: normalizeRegion(value.top, defaults.top), bottom: normalizeRegion(value.bottom, defaults.bottom),
    center: { ...normalizeRegion(value.center, defaults.center), enabled: center.enabled !== false },
    geometry: {
      shape: geometry.shape === 'ellipse' ? 'ellipse' : 'circle', topSweep: num(geometry.topSweep, 120, 30, 180),
      bottomSweep: num(geometry.bottomSweep, 110, 30, 180),
      topSide: geometry.topSide === 'inside' ? 'inside' : 'outside', bottomSide: geometry.bottomSide === 'inside' ? 'inside' : 'outside',
      padding: num(geometry.padding, 18, 0, 100),
      autoFit: { enabled: badgeAutoFit.enabled !== false, minSize: badgeAutoFitMin, maxSize: badgeAutoFitMax },
      curveVersion: geometry.curveVersion === 2 ? 2 : 1,
    },
    separator: { kind, color: color(separator.color, defaults.separator.color), size: num(separator.size, 8, 2, 40) },
    appearance: normalizeAppearance(value.appearance, '#111827'),
  };
}

export function normalizePolicy(raw: unknown): TypographyProductPolicy {
  const value = record(raw);
  return {
    inherit: value.inherit !== false,
    enabled: value.enabled !== false,
    allowedLayouts: Array.isArray(value.allowedLayouts) ? value.allowedLayouts.filter((mode): mode is TypographyLayoutMode => LAYOUTS.includes(mode as TypographyLayoutMode)) : [...LAYOUTS],
    allowedEffects: Array.isArray(value.allowedEffects) ? value.allowedEffects.filter((effect): effect is TypographyEffect => EFFECTS.includes(effect as TypographyEffect)) : [...EFFECTS],
    allowedPresetIds: Array.isArray(value.allowedPresetIds) ? value.allowedPresetIds.map(String) : [],
    allowedFonts: Array.isArray(value.allowedFonts) ? value.allowedFonts.map(String) : [],
    minArc: num(value.minArc, -360, -360, 360), maxArc: num(value.maxArc, 360, -360, 360), maxChars: Math.round(num(value.maxChars, 40, 1, 500)),
  };
}

/** Current layout geometry; stamped on new layouts and on any layout edit. */
export const CURVE_VERSION = 2 as const;

/**
 * A preset or new layer always takes the current geometry: only layouts saved on a design keep
 * the generation they were approved with.
 */
export function withCurrentCurves<T extends TypographyConfig | TypographyBadgeConfig>(config: T): T {
  if ('layout' in config) return { ...config, layout: { ...config.layout, curveVersion: CURVE_VERSION } };
  return { ...config, geometry: { ...config.geometry, curveVersion: CURVE_VERSION } };
}

export const TYPOGRAPHY_LAYOUTS = LAYOUTS;
export const TYPOGRAPHY_EFFECTS = EFFECTS;
